import {
  skipToken,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from '#/api/client';
import { mutationNotif } from '#/utils/mutationNotif';

import {
  MESSAGE_TEMPLATE_ENDPOINT_BASE,
  MESSAGE_QUERY_KEY_NAMESPACE,
} from './utils.ts';
import { MessageTemplateSchema } from '../model/index.ts';
import { z } from 'zod';

const MessageResponseSchema = MessageTemplateSchema;

const MessageMutationResponseSchema = z.object({
  data: MessageTemplateSchema,
});

async function fetchMessage(id: string) {
  const response = await apiClient.get(
    `${MESSAGE_TEMPLATE_ENDPOINT_BASE}/${id}`,
  );

  return response.data;
}

export function useMessage(id?: string) {
  return useQuery({
    queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplate', id],
    enabled: !!id,
    queryFn: id
      ? async () => {
          const response = await fetchMessage(id);

          const parsed = MessageResponseSchema.safeParse(response.data);

          if (!parsed.success) {
            console.error(parsed.error);

            throw new Error('Invalid message response from server');
          }

          return parsed.data;
        }
      : skipToken,
  });
}

async function approveMessage(id: string) {
  const response = await apiClient.post(
    `${MESSAGE_TEMPLATE_ENDPOINT_BASE}/${id}/approve`,
  );
  const parsed = MessageMutationResponseSchema.safeParse(response.data);
  if (!parsed.success) throw new Error('Invalid approve response from server');
  return parsed.data;
}

async function unapproveMessage(id: string) {
  const response = await apiClient.post(
    `${MESSAGE_TEMPLATE_ENDPOINT_BASE}/${id}/unapprove`,
  );
  const parsed = MessageMutationResponseSchema.safeParse(response.data);
  if (!parsed.success)
    throw new Error('Invalid unapprove response from server');
  return parsed.data;
}

async function startRevokingMessage(id: string) {
  const response = await apiClient.post(
    `${MESSAGE_TEMPLATE_ENDPOINT_BASE}/${id}/start_revoking`,
  );
  const parsed = MessageMutationResponseSchema.safeParse(response.data);
  if (!parsed.success)
    throw new Error('Invalid start_revoking response from server');
  return parsed.data;
}

async function deleteMessage(id: string) {
  await apiClient.delete(`${MESSAGE_TEMPLATE_ENDPOINT_BASE}/${id}`);
}

export function useApproveMessage(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return id && (await approveMessage(id));
    },
    mutationKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplate', id],
    onMutate: () => {
      if (!id) return;
      mutationNotif.loading(id, { title: '核准訊息', message: '核准中...' });
      return { notifId: id };
    },
    onSuccess: (_, __, context) => {
      queryClient.invalidateQueries({
        queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplate', id],
      });
      queryClient.invalidateQueries({
        queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplates'],
      });

      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已核准');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}

export function useUnapproveMessage(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return id && (await unapproveMessage(id));
    },
    mutationKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplate', id],
    onMutate: () => {
      if (!id) return;
      mutationNotif.loading(id, { title: '取消核准', message: '取消中...' });
      return { notifId: id };
    },
    onSuccess: (_, __, context) => {
      queryClient.invalidateQueries({
        queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplate', id],
      });
      queryClient.invalidateQueries({
        queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplates'],
      });

      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已取消核准');
    },
    onError: (error, __, context) => {
      console.error(error);

      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}

export function useStartRevokingMessage(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return id && (await startRevokingMessage(id));
    },
    mutationKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplate', id],
    onMutate: () => {
      if (!id) return;
      mutationNotif.loading(id, { title: '開始撤銷', message: '撤銷中...' });
      return { notifId: id };
    },
    onSuccess: (_, __, context) => {
      queryClient.invalidateQueries({
        queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplate', id],
      });
      queryClient.invalidateQueries({
        queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplates'],
      });

      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已開始撤銷');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}

export function useDeleteMessage(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return id && (await deleteMessage(id));
    },
    mutationKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplate', id],
    onMutate: () => {
      if (!id) return;
      mutationNotif.loading(id, { title: '刪除訊息', message: '刪除中...' });
      return { notifId: id };
    },
    onSuccess: (_, __, context) => {
      queryClient.invalidateQueries({
        queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplate', id],
      });
      queryClient.invalidateQueries({
        queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplates'],
      });

      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已刪除');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}

export function useIsMessageMutating(id: string | undefined) {
  const isMessageMutating = useMutationState({
    // this mutation key needs to match the mutation key of the given mutation (see above)
    filters: {
      mutationKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplate', id],
    },
    select: (mutation) => mutation.state.status === 'pending',
  });

  if (!id) {
    return false;
  }

  return isMessageMutating.some((v) => !!v);
}
